import { Box, Button, Dialog, IconButton, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CountCard from '../../common/CountCard'
import CustomTable from '../../common/custom-table/CustomTable'
import CustomSelectField from '../../common/CustomSelectField'
import MultiLineGraph from '../../common/MultiLineGraph'
import { Icons, formatDate } from '../../../utils/utilities'
import { exportQuizMonthlyReportStudents } from '../../../utils/exam-utilities'
import api from '../../../service/api'

const graphSeriesConfig = [
    { key: "attempts", label: "Attempts", color: "#134fcf" },
    { key: "students", label: "Students", color: "#00a76f" },
    { key: "average_percentage", label: "Average %", color: "#f59e0b" },
]

const studentColumns = [
    { field: "name", name: "Name", renderCell: std => std.name?.toLowerCase(), cellStyles: { textTransform: "capitalize" }, align: "left", width: "140px", sortable: true, searchable: true, filterable: true },
    { field: "student_id", name: "AUID", align: "left", width: "140px", sortable: true, searchable: true, filterable: true },
    { field: "usn", name: "USN", align: "left", width: "140px", sortable: true, searchable: true, filterable: true },
    { field: "branch_name", filterable: true, sortable: true, name: "Branch", align: "left", width: "100px" },
    { field: "section", filterable: true, sortable: true, name: "Section", align: "center", width: "90px" },
    { field: "ac_year", filterable: true, sortable: true, name: "Academic Year", align: "center", width: "120px" },
    { field: "attempts", name: "Attempts", align: "center", width: "100px", sortable: true, dataType: "number" },
    { field: "quizzes_attempted", name: "Quizzes Attempted", align: "center", width: "160px", sortable: true, dataType: "number" },
    { field: "average_percentage", name: "Avg %", align: "center", width: "100px", sortable: true, dataType: "number", renderCell: row => `${row.average_percentage}%` },
    { field: "highest_percentage", name: "Highest %", align: "center", width: "110px", sortable: true, dataType: "number", renderCell: row => `${row.highest_percentage}%` },
    { field: "lowest_percentage", name: "Lowest %", align: "center", width: "110px", sortable: true, dataType: "number", renderCell: row => `${row.lowest_percentage}%` },
    { field: "last_attempted_at", name: "Last Attempted", align: "left", width: "170px", sortable: true, renderCell: row => formatDate( row.last_attempted_at, { showTime: true } ) },
]

const QuizMonthlyReportDialog = ( { state, setState } ) => {

    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( false )
    const [report, setReport] = useState( null )
    const [monthValue, setMonthValue] = useState( "last30" )

    const { palette, border, layout } = useTheme()

    const monthOptions = useMemo( () => {
        const now = dayjs()
        return [
            { value: "last30", label: "Last 30 days" },
            { value: now.format( 'YYYY-MM' ), label: "This month" },
            { value: now.subtract( 1, 'month' ).format( 'YYYY-MM' ), label: "Last month" },
            { value: `${now.subtract( 2, 'month' ).format( 'YYYY-MM' )}-${now.subtract( 1, 'month' ).format( 'YYYY-MM' )}`, label: "Last 2 months" },
            { value: `${now.subtract( 3, 'month' ).format( 'YYYY-MM' )}-${now.subtract( 1, 'month' ).format( 'YYYY-MM' )}`, label: "Last 3 months" },
        ]
    }, [] )

    const fetchReport = useCallback( async ( month ) => {
        setLoading( true )
        setError( false )
        try {
            const { data: { data } } = await api.quiz.fetchMonthlyReport( { month: ( !month || month === "last30" ) ? '' : ( month || "" ) } )
            setReport( data )
        } catch ( err ) {
            setError( true )
            toast( err?.response?.data?.message || "Something went wrong while fetching the monthly report!" )
        } finally {
            setLoading( false )
        }
    }, [] )

    useEffect( () => {
        if ( state )
            fetchReport( "" )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state] )

    const handleMonthChange = ( e ) => {
        const value = e.target.value
        setMonthValue( value )
        fetchReport( value )
    }

    const handleClose = () => setState( false )

    return (
        <Dialog open={state} onClose={handleClose} slotProps={{
            paper: { sx: { width: "95%", maxWidth: "1200px", background: palette.form.formCardBg } }
        }}>
            <Box position="sticky" borderBottom={border[1]} top="0" bgcolor={palette.form.formCardBg} sx={{ zIndex: 5 }} display="flex" justifyContent="space-between" gap="20px" alignItems="center" padding="10px 20px">
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Quiz Monthly Report</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>{report?.filter?.label ? `Showing data for ${report.filter.label.toLowerCase()}` : "Overview of quiz participation and performance"}</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CustomSelectField
                        disabled={loading}
                        size="small"
                        value={monthValue}
                        onChange={handleMonthChange}
                        data={monthOptions}
                        itemValueKey="value"
                        itemIdentityKey="value"
                        renderOptionText={opt => opt.label}
                        renderValue={selected => monthOptions.find( opt => opt.value === selected )?.label || "Select time range"}
                        placeholder="Select time range"
                        selectStyles={{ minWidth: "180px" }}
                    />
                    <IconButton onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
                </Box>
            </Box>

            <Box padding={layout.pagePadding} display="flex" flexDirection="column" gap="20px">
                {loading && <DataLoadingSpinner padding='40px 0' waitingMessage="Fetching monthly report..." />}

                {!loading && error && <Box display="flex" flexDirection="column" alignItems="center" gap="10px" padding="40px 0">
                    <Typography color="error" variant="subtitle2">Failed to load the monthly report.</Typography>
                    <Button onClick={() => fetchReport( monthValue )} variant="outlined" color="greyed" startIcon={Icons.default.Replay}>Retry</Button>
                </Box>}

                {!loading && !error && report && <>
                    <Box display="grid" gap={layout.cardsGap} width="100%" gridTemplateColumns={{ md: "repeat(4,1fr)", sm: "repeat(2,1fr)", xs: "repeat(1,1fr)" }}>
                        <CountCard label="Quizzes Conducted" value={report.counts?.quizzes ?? 0} icon={Icons.default.QuizIcon} />
                        <CountCard label="Quiz Instances" value={report.counts?.quiz_instances ?? 0} icon={Icons.default.Calendar} />
                        <CountCard label="Students Participated" value={report.counts?.students_participated ?? 0} icon={Icons.default.Group} />
                        <CountCard label="Attempts Submitted" value={report.counts?.attempts_submitted ?? 0} secondary={report.counts?.attempts_started ?? 0} secondaryLabel="Started" icon={Icons.default.AssignmentIcon} />
                        <CountCard label="Average Percentage" value={`${report.counts?.average_percentage ?? 0}%`} secondary={report.counts?.average_score ?? 0} secondaryLabel="Avg marks" icon={Icons.default.TrendingUp} />
                        <CountCard label="Pass Percentage" value={`${report.counts?.pass_percentage ?? 0}%`} secondary={report.counts?.passed_attempts ?? 0} secondaryLabel="Passed attempts" icon={Icons.default.CheckCircleIcon} />
                        <CountCard label="Completion Rate" value={`${report.counts?.completion_rate ?? 0}%`} icon={Icons.default.EmojiEvents} />
                        <CountCard label="Avg Attempts / Student" value={report.counts?.average_attempts_per_student ?? 0} icon={Icons.default.People} />
                    </Box>

                    {report.graph?.points?.length > 0 && <MultiLineGraph
                        title="Quiz Participation Trend"
                        data={report.graph.points}
                        seriesConfig={graphSeriesConfig}
                        xAxisKey="label"
                        dateFormat={report.graph.points[0]?.label?.length === 7 ? "%Y-%m" : "%Y-%m-%d"}
                        height={320}
                    />}

                    <Box>
                        <Typography variant="subtitle2" fontWeight="600" marginBottom="10px">Student Performance</Typography>
                        <CustomTable
                            tableKey="quiz-monthly-report-students"
                            dataIdKey="student_id"
                            columns={studentColumns}
                            data={report.students || []}
                            globalSearch="Search by USN"
                            containerStyles={{ maxHeight: "400px" }}
                            customActions={[{
                                type: 'button',
                                label: 'Export to Excel',
                                color: 'secondary',
                                startIcon: Icons.default.DownloadingIcon,
                                onClick: ( processedRows ) => exportQuizMonthlyReportStudents( processedRows, report?.filter?.label ),
                            }]}
                        />
                    </Box>
                </>}
            </Box>
        </Dialog>
    )
}

export default QuizMonthlyReportDialog
